import { useNavigate } from 'react-router'
import { Helmet } from 'react-helmet'
import { useCallback, useEffect, useState } from 'react'
import { KycInitForm } from './components/KycInitForm'
import { useKycInitLogic } from './useKycInitLogic'
import { APP_ROUTES } from '@/common/constants'
import { APP } from '@/common/strings'
import { ONBOARDING_COPY } from '@/locales/en'
import { analytics } from '@/common/analytics/analytics'
import OnboardingKycSkippedAnalytics from '@/common/analytics/events/onboarding/OnboardingKycSkippedAnalytics'
import { OnboardingRedirectModal } from '@/onboarding/onboardingRedirect'
import { KycProviders } from '@/kyc/types'
import { toast } from 'sonner'
import useAccountStore from '@/account/accountStore'
import { useOnboardingProgress } from '../../useOnboardingProgress'

export function KycInitPage() {
    const navigate = useNavigate()
    const { progress } = useOnboardingProgress()
    const logic = useKycInitLogic()
    const activeAccountId = useAccountStore((s) => s.activeAccountId)
    const [openRedirect, setOpenRedirect] = useState<boolean>(false)

    const handleSkip = useCallback(() => {
        OnboardingKycSkippedAnalytics.track({ activeAccountId })
        navigate('/')
    }, [activeAccountId, navigate])

    const handleStart = useCallback(() => {
        if (!logic.data || !activeAccountId) return

        if (logic.data.provider === KycProviders.FRANKIEONE) {
            analytics.track('onboarding_kyc_initiated', { provider: KycProviders.FRANKIEONE, activeAccountId })
            navigate(APP_ROUTES.KYC)
            return
        }

        setOpenRedirect(true)
        analytics.track('onboarding_kyc_initiated', { provider: KycProviders.SWIFTID, activeAccountId })
        logic.onStart()
    }, [logic, activeAccountId, navigate])

    // Guard 1: Auto-skip if canInitiate is false
    useEffect(() => {
        if (!logic.data || logic.loading) return
        if (!logic.data.canInitiate) {
            handleSkip()
        }
    }, [logic.data, logic.loading, handleSkip])

    // Guard 2: On successful SWIFTID init → redirect to external URL
    useEffect(() => {
        if (!logic.isSuccess || logic.saving || !logic.mutationData) return

        if (logic.mutationData.provider === KycProviders.FRANKIEONE) {
            navigate(APP_ROUTES.KYC)
            return
        }

        if (!logic.mutationData.url) return
        window.location.href = logic.mutationData.url
    }, [logic.isSuccess, logic.saving, logic.mutationData, navigate])

    // Guard 3: On error → show toast and close modal
    useEffect(() => {
        if (!logic.isError) return

        analytics.trackWithDefaults('onboarding_kyc_initiate_failed', { activeAccountId })
        setOpenRedirect(false)
        toast.error(ONBOARDING_COPY.kycInit.errorToast)
    }, [logic.isError, activeAccountId])

    return (
        <>
            <Helmet>
                <title>
                    {ONBOARDING_COPY.kycInit.title} | {APP.title}
                </title>
            </Helmet>
            <KycInitForm
                progress={progress}
                accountType={logic.data?.accountType}
                provider={logic.data?.provider}
                canInitiate={logic.data?.canInitiate}
                loading={logic.loading}
                saving={logic.saving}
                disabled={!activeAccountId}
                onSkip={handleSkip}
                onStart={handleStart}
            />
            <OnboardingRedirectModal open={openRedirect} />
        </>
    )
}
